import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import socket from '../utils/socket';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * `G` instant-access badge shown inside the DWS green header.
 * Click → dropdown of MB7..MB11 with per-MB "pending" (not mark-as-paid) counts.
 * Auto-refresh every 5s so new coin cuts bubble up without a page reload.
 *
 * "Unread" here means `withdraw_entries.status == "pending"` for that MB's group.
 */
const InstantAccessG = ({ variant = 'dark' }) => {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState({ total: 0, by_mb: {}, groups: [] });
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    // Fix doc #5 — instant-cache: paint last-known MB group summary immediately, then refresh.
    const cached = readInstantCache('mb_pending_summary', user?.id);
    if (cached && !cancelled) setSummary(cached);
    const load = async () => {
      try {
        const res = await axios.get(`${API}/mb-groups/pending-summary`, {
          params: { user_id: user?.id },
        });
        if (!cancelled) {
          const data = res.data || { total: 0, by_mb: {}, groups: [] };
          setSummary(data);
          writeInstantCache('mb_pending_summary', user?.id, data);
        }
      } catch (_) { /* ignore transient */ }
    };
    load();
    // Poll every 12s as a fallback only; real-time bumps come via `pw_updated`
    // below, so the poll no longer needs to be aggressive (cuts request fan-out).
    const t = setInterval(() => { if (document.visibilityState === 'hidden') return; load(); }, 12000);
    let debounce = null;
    const onPwUpdated = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => load(), 200);
    };
    socket.on('pw_updated', onPwUpdated);
    return () => {
      cancelled = true;
      clearInterval(t);
      if (debounce) clearTimeout(debounce);
      socket.off('pw_updated', onPwUpdated);
    };
  }, [user?.id]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [open]);

  // P3/P4 — only one of N/G/PT/PW open at a time.
  useEffect(() => {
    const onOther = (e) => { if (e.detail !== 'G') setOpen(false); };
    window.addEventListener('dws:ia-open', onOther);
    return () => window.removeEventListener('dws:ia-open', onOther);
  }, []);

  // Re-open the G dropdown after back-navigation from a group opened via this menu.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('g_reopen') === '1') {
        sessionStorage.removeItem('g_reopen');
        setOpen(true);
      }
    } catch (_) {}
  }, []);

  // Batch H — Ctrl+G (no following digit) opens this slider overview,
  // dispatched globally by GlobalKeyboardShortcuts.jsx.
  useEffect(() => {
    const onOpenG = () => { setOpen(true); window.dispatchEvent(new CustomEvent('dws:ia-open', { detail: 'G' })); };
    window.addEventListener('dws:kbnav-open-g', onOpenG);
    return () => window.removeEventListener('dws:kbnav-open-g', onOpenG);
  }, []);

  const total = summary.total || 0;
  const groups = (summary.groups || []).slice().sort((a, b) => (a.mb_setup || '').localeCompare(b.mb_setup || '', undefined, { numeric: true }));

  return (
    <div className="relative" ref={ref} data-testid="instant-access-g">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => { const nv = !v; if (nv) window.dispatchEvent(new CustomEvent('dws:ia-open', { detail: 'G' })); return nv; }); }}
        className={variant === 'light'
          ? "relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#00a884] hover:bg-[#008f70] text-white font-black text-base shadow-md transition-colors"
          : "relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 text-white font-black text-base border border-white/30 shadow-inner transition-colors"}
        data-testid="instant-access-g-btn"
        title="Instant access to MB groups"
      >
        G
        {total > 0 && (
          <span
            className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow"
            data-testid="instant-access-g-total"
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-[80] bg-white text-slate-800 rounded-xl shadow-2xl border border-slate-200 w-[min(260px,calc(100vw-24px))] py-2" data-testid="instant-access-g-menu">
          <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500 border-b border-slate-100 mb-1">
            Withdraw Groups · Pending
          </div>
          {groups.length === 0 ? (
            <div className="px-3 py-4 text-xs text-slate-400 text-center">No MB groups yet</div>
          ) : (
            groups.map((g) => (
              <button
                key={g.mb_setup}
                onClick={() => {
                  setOpen(false);
                  // Mark that G should re-open when the user returns via back button.
                  try { sessionStorage.setItem('g_reopen', '1'); } catch (_) {}
                  // Preserve origin so the group-chat back-button returns to the
                  // exact tab the user came from AND re-opens the 1-on-1 chat that
                  // was open in the right panel. selectedChatId is the dashboard's
                  // component state (not in location.state), so we read the value
                  // the dashboard mirrors into sessionStorage.
                  let openChatId = null;
                  try { openChatId = sessionStorage.getItem('dws_open_chat_id') || null; } catch (_) {}
                  navigate(`/groups/${g.group_id}`, {
                    state: {
                      from: location.pathname,
                      fromState: {
                        ...(location.state || {}),
                        ...(openChatId ? { selectedChatId: openChatId } : {}),
                      },
                    },
                  });
                }}
                className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-emerald-50"
                data-testid={`instant-access-mb-${g.mb_setup}`}
              >
                <span className="font-semibold text-slate-800">{g.mb_setup}</span>
                {(g.pending || 0) > 0 ? (
                  <span className="min-w-[22px] h-[20px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {g.pending}
                  </span>
                ) : (
                  <span className="text-[10px] text-slate-400">clear</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default InstantAccessG;
